// ─────────────────────────────────────────────────────────────────────────────
// Tratamento de texto — escapes, normalização, comparação e quebra em pedaços.
//
// Tudo aqui é função pura: entra texto, sai texto. Nenhuma toca a tela.
// ─────────────────────────────────────────────────────────────────────────────

/** Escapa HTML. Usado antes de qualquer texto vindo de fora entrar na página. */
export function escHtml(str) {
  return String(str || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/** Nome sem acento, minúsculo e com espaços normalizados — para comparar. */
export function normalizarNome(nome) {
  return (nome || '')
    .normalize('NFD').replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .trim()
    .replace(/\s+/g, ' ');
}

/**
 * Normalização mais agressiva, para casar textos de origens diferentes:
 * tira acento, troca pontuação por espaço e colapsa espaços.
 */
export function normalizarParaComparar(str) {
  if (!str) return '';
  return str
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]/g, ' ')
    .trim()
    .replace(/\s+/g, ' ');
}

/** Número que abre a string ("36 - Em atendimento" → "36"). null se não houver. */
export function obterCodigoInicial(str) {
  const match = String(str).match(/^(\d+)/);
  return match ? match[1] : null;
}

/**
 * Acha, numa lista de opções, a que melhor corresponde a um texto sugerido.
 *
 * Existe porque a classificação vem do MySuite e da IA em grafias que não batem
 * exatamente com as opções do banco ("2 - Dúvida" vs "Duvida (2)"). A pontuação
 * pesa, em ordem: código inicial igual (+100) ou diferente (-50), um conter o
 * outro (-50) e palavras em comum (até +30).
 *
 * `opcoes` é uma lista de { valor, texto }. Devolve a opção ou null.
 */
export function encontrarMelhorMatch(sugestao, opcoes) {
  if (!sugestao) return null;

  const normalSugestao = normalizarParaComparar(sugestao);
  const numSugestao = obterCodigoInicial(sugestao);

  let melhorOpcao = null;
  let pontuacaoMaxima = -1;

  for (const opt of opcoes) {
    if (!opt.valor && opt.valor !== 0) continue;   // pula o "Selecione..."

    const normalOpcao = normalizarParaComparar(opt.texto);
    const numOpcao = obterCodigoInicial(opt.texto);

    // Match perfeito já resolve.
    if (normalSugestao === normalOpcao) return opt;

    let pontuacao = 0;

    if (numSugestao && numOpcao && numSugestao === numOpcao) pontuacao += 100;
    else if (numSugestao && numOpcao && numSugestao !== numOpcao) pontuacao -= 50;

    if (normalSugestao.includes(normalOpcao) || normalOpcao.includes(normalSugestao)) pontuacao -= 50;

    const palavrasSugestao = normalSugestao.split(' ').filter(w => w.length > 2);
    const palavrasOpcao = normalOpcao.split(' ').filter(w => w.length > 2);
    const comuns = palavrasOpcao.filter(p => palavrasSugestao.includes(p)).length;
    if (palavrasOpcao.length > 0) pontuacao += (comuns / palavrasOpcao.length) * 30;

    if (pontuacao > pontuacaoMaxima && pontuacao > 0) {
      pontuacaoMaxima = pontuacao;
      melhorOpcao = opt;
    }
  }

  return melhorOpcao;
}

/**
 * Quebra um texto em pedaços de no máximo `limite` caracteres.
 *
 * O MySuite tem teto por interação, então uma formalização longa precisa sair
 * em partes. A quebra respeita parágrafo primeiro, palavra depois, e só corta
 * no meio de uma palavra quando ela sozinha já passa do limite.
 */
export function dividirTextoEmChunks(texto, limite = 350) {
  if (!texto) return ['Interação vazia.'];
  if (texto.length <= limite) return [texto];

  const paragrafos = texto.split(/\r?\n/);
  const chunks = [];
  let chunkAtual = '';

  for (const paragrafo of paragrafos) {
    if (paragrafo.trim() === '') {
      if (chunkAtual && chunkAtual.length + 1 <= limite) chunkAtual += '\n';
      continue;
    }

    if (paragrafo.length > limite) {
      if (chunkAtual) { chunks.push(chunkAtual.trim()); chunkAtual = ''; }

      let subChunk = '';
      for (const palavra of paragrafo.split(' ')) {
        const tamanhoEsperado = subChunk ? subChunk.length + 1 + palavra.length : palavra.length;
        if (tamanhoEsperado > limite) {
          if (subChunk) chunks.push(subChunk.trim());

          if (palavra.length > limite) {
            let parte = palavra;
            while (parte.length > limite) {
              chunks.push(parte.substring(0, limite));
              parte = parte.substring(limite);
            }
            subChunk = parte;
          } else {
            subChunk = palavra;
          }
        } else {
          subChunk = subChunk ? `${subChunk} ${palavra}` : palavra;
        }
      }
      if (subChunk) chunkAtual = subChunk;
    } else {
      const separador = chunkAtual ? '\n' : '';
      if ((chunkAtual + separador + paragrafo).length > limite) {
        if (chunkAtual) chunks.push(chunkAtual.trim());
        chunkAtual = paragrafo;
      } else {
        chunkAtual = chunkAtual + separador + paragrafo;
      }
    }
  }

  if (chunkAtual) chunks.push(chunkAtual.trim());
  return chunks.filter(c => c.length > 0);
}

// ── Interações do ticket ─────────────────────────────────────────────────────

/**
 * "Aviso de alteração" é quando TODAS as linhas não-vazias são "Campo -> valor".
 * Essas interações são mudanças automáticas de classificação/previsão/status, e
 * a tela as mostra como faixa de aviso, não como mensagem de alguém.
 */
export function todasLinhasSaoAlteracoes(texto) {
  const linhas = (texto || '').split('\n').map(l => l.trim()).filter(l => l.length > 0);
  return linhas.length > 0 && linhas.every(l => l.includes('->'));
}

/**
 * Prepara a descrição de uma interação para exibição.
 *
 * O MySuite envia texto com um pouco de HTML dentro (<br>, <b>) e os anexos
 * ora como URL crua, ora embrulhados numa âncora com <font> e <u>. Se
 * escapássemos tudo, as tags apareceriam como texto na tela; se não
 * escapássemos nada, qualquer HTML de fora entraria na página.
 *
 * A saída é HTML e vai para dangerouslySetInnerHTML — por isso a ordem importa:
 *   1. extrai as URLs de anexo e deixa um marcador no lugar;
 *   2. escapa TODO o resto;
 *   3. devolve só <br> e <b>;
 *   4. troca os marcadores por <img>.
 */
export function formatarDescricaoInteracao(texto) {
  let t = String(texto || '');
  const imgs = [];

  const attachSrc = "https?:\\/\\/areadocliente\\.space-cloud\\.tech\\/api\\/attachments\\/[^\\s<>\"']+";

  // 1) Âncora completa envolvendo o anexo (tags <font>/<u> opcionais) → marcador
  t = t.replace(
    new RegExp("(?:<font[^>]*>)?\\s*<a\\s+href=['\"]?(" + attachSrc + ")['\"]?[^>]*>[\\s\\S]*?<\\/a>\\s*(?:<\\/font>)?", 'gi'),
    (_m, url) => { imgs.push(url); return `@@ORIONIMG${imgs.length - 1}@@`; }
  );
  // 2) URLs cruas remanescentes (sem âncora)
  t = t.replace(new RegExp(attachSrc, 'gi'), (url) => {
    imgs.push(url);
    return `@@ORIONIMG${imgs.length - 1}@@`;
  });

  let out = escHtml(t)
    .replace(/&lt;br\s*\/?&gt;/gi, '<br>')
    .replace(/&lt;b&gt;/gi, '<b>')
    .replace(/&lt;\/b&gt;/gi, '</b>');

  out = out.replace(/@@ORIONIMG(\d+)@@/g, (_m, i) => {
    const url = imgs[Number(i)];
    return `<img class="interacao-img js-lightbox" src="${url}" data-full="${url}" alt="Anexo do ticket" title="Clique para ampliar">`;
  });

  return out;
}

/**
 * Separa o cabeçalho "[Usuário Logado: Fulano]" do corpo da interação.
 * Devolve { autor, corpo } — autor é null quando o cabeçalho não existe.
 */
export function separarUsuarioLogado(descricao) {
  const m = String(descricao || '').match(/^\[Usuário Logado:\s*([^\]]+)\]\s*\n?([\s\S]*)/);
  if (!m) return { autor: null, corpo: String(descricao || '') };
  return { autor: m[1].trim(), corpo: m[2] };
}
